import React, { useEffect, createElement } from 'react';
import { Star, Clock, Frown, Repeat, Brain, AlertTriangle, CheckCircle2, Heart, Gift, Sparkles, Lock, Infinity as InfinityIcon } from 'lucide-react';
import { PurchaseButton } from './components/PurchaseButton';
import { CountdownTimer } from './components/CountdownTimer';
import { SocialProofPopup } from './components/SocialProofPopup';
import { AccordionItem } from './components/Accordion';
import { TestimonialsCarousel } from './components/TestimonialsCarousel';
export function App() {
  // Initialize Meta Pixel
  useEffect(() => {
    const pixelId = '2782582688765590';
    const w = window as any;
    // Set up the fbq queue stub (safe to re-run; guarded below)
    if (!w.fbq) {
      const n: any = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      w.fbq = n;
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      // Inject the Facebook events script, appending to <head> safely
      // instead of relying on an existing <script> tag being present.
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const target = document.head || document.body;
      if (target) {
        target.appendChild(script);
      }
    }
    w.fbq('init', pixelId);
    w.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-brand-babypink font-sans text-brand-black selection:bg-brand-pink selection:text-white pb-20" data-id="element-0" data-mp="33mf0loc3tncg">
      <SocialProofPopup data-id="element-1" data-mp="38kqgafp2ys2l" />

      {/* HERO SECTION */}
      <header className="pt-12 pb-16 px-4 max-w-4xl mx-auto text-center" data-id="element-2" data-mp="3dkuws5pznmst">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-brand-black mb-8 leading-tight" data-id="element-3" data-mp="3dirv2ofh4589">
          Deja de improvisar tus clases de barre
        </h1>

        <a href="https://barreresuelto.impultienda.ar" className="block relative w-full max-w-2xl mx-auto mb-8 rounded-2xl overflow-hidden shadow-soft transition-transform duration-300 hover:scale-[1.02] cursor-pointer" data-id="element-4" data-mp="stccggqermtp">
          <img src="https://cdn.magicpatterns.com/uploads/x2CBHN4wbevecgoL629dDW/imagen_sin_fondo.png" alt="Barre Resuelto Bundle" className="w-full h-auto object-cover bg-white" data-id="element-5" data-mp="d4e6dvbvvgxr" />
        </a>

        <h2 className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-6 leading-relaxed" data-id="element-6" data-mp="3gm64zskiyz4x">
          Todo lo que necesitás para crear clases de barre más organizadas,
          creativas y seguras, sin perder horas planificando.
        </h2>

        <div className="flex items-center justify-center gap-2 mb-8" data-id="element-7" data-mp="3lkheu3atl9n5">
          <div className="flex text-yellow-400" data-id="element-8" data-mp="3lj69w05n1vug">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} fill="currentColor" data-id="element-9" data-mp="2xtbvvf9l218m" data-mpi={i} />)}
          </div>
          <span className="font-semibold text-gray-700" data-id="element-10" data-mp="2sv0m14jafqqe">4.9/5 estrellas</span>
        </div>

        <div className="flex flex-col items-center" data-id="element-11" data-mp="18czn0iw5pdmh">
          <PurchaseButton data-id="element-12" data-mp="1d4hnz6y6je7g" />
          <p className="text-brand-pink font-bold mt-4 uppercase tracking-wide text-sm" data-id="element-13" data-mp="1d4rcsr7hnfbk">
            Oferta especial solo por hoy
          </p>
        </div>
      </header>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-14" data-mp="13fq13d7z7k3e">
        <div className="max-w-5xl mx-auto" data-id="element-15" data-mp="13cbwrzfr50tb">
          <div className="text-center mb-12" data-id="element-16" data-mp="1r3zdxpn7oxie">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-17" data-mp="1r266w98osb9v">
              ¿Te suena familiar esta situación?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-18" data-mp="1i4ceh9e1m1rz">
              Sabemos lo frustrante que es llegar a una clase de barre sin tener
              claro qué ejercicios hacer, sintiendo que siempre terminás
              improvisando y repitiendo lo mismo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-19" data-mp="aq9aml3x2d9t">
            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center" data-id="element-20" data-mp="qwvrwf96wp6w">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-21" data-mp="m4ujj4f57im5">
                <Clock size={32} data-id="element-22" data-mp="h83ys3se7yue" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-23" data-mp="c8j1dxsvfyjs">
                Pérdida de Tiempo
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-24" data-mp="19w9mmzequ3yg">
                Pasás horas buscando inspiración en Instagram, Pinterest o
                YouTube, armando clases a último momento en lugar de descansar,
                disfrutar tu tiempo libre o dedicarte a hacer crecer tu negocio.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center" data-id="element-25" data-mp="vvyvrwdfopda">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-26" data-mp="3gly1dz70amcq">
                <Frown size={32} data-id="element-27" data-mp="1w4osw9ym7gs8" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-28" data-mp="1w5zuc34l8cg6">
                Estrés e Improvisación
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-29" data-mp="203nuplp1uxfg">
                Llegás a la clase sin una estructura clara, dudando qué
                ejercicio sigue y sintiendo que todo depende de improvisar sobre
                la marcha.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center" data-id="element-30" data-mp="2eun2wvlz3jyi">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-31" data-mp="2nv34fzj5oh0q">
                <Repeat size={32} data-id="element-32" data-mp="2sufne96wp3y1" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-33" data-mp="2stwgrompi1zb">
                Falta de Variedad
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-34" data-mp="tjy1yyjt2amz">
                Terminás utilizando siempre los mismos movimientos y secuencias,
                con miedo a que tus alumnas se aburran o sientan que todas las
                clases son iguales.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center md:col-span-2 lg:col-span-1" data-id="element-35" data-mp="1wuu0520p5ao8">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-36" data-mp="25rul0yok4qmx">
                <Brain size={32} data-id="element-37" data-mp="2koveb6mectni" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-38" data-mp="2frlecx1dszlf">
                Agotamiento Mental
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-39" data-mp="2tj00vduhzix8">
                Más allá de enseñar, tenés que pensar constantemente qué hacer,
                cómo combinar ejercicios y cómo adaptar cada clase. Esa carga
                mental termina agotándote.
              </p>
            </div>

            <div className="bg-brand-babypink p-8 rounded-3xl shadow-sm border border-brand-pastelpink/50 flex flex-col items-center text-center md:col-span-2 lg:col-span-1 lg:col-start-2" data-id="element-40" data-mp="2yiw5nfb0redu">
              <div className="bg-white p-4 rounded-full mb-4 shadow-sm text-brand-pink" data-id="element-41" data-mp="3n2zkfdfgkaay">
                <AlertTriangle size={32} data-id="element-42" data-mp="385yr55hmc7ad" />
              </div>
              <h3 className="font-serif font-bold text-xl mb-3" data-id="element-43" data-mp="387jn8zjkzyac">
                Menos Confianza
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-44" data-mp="2zd5950asetst">
                Cuando no tenés un sistema, es fácil sentir inseguridad y
                preguntarte si realmente estás ofreciendo la mejor experiencia
                posible a tus alumnas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-16 px-4 bg-brand-lilac/30" data-id="element-45" data-mp="2uebrcvogwgmu">
        <div className="max-w-5xl mx-auto" data-id="element-46" data-mp="2ge119uuzf62q">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-12 text-center" data-id="element-47" data-mp="2ggds6xipwqrx">
            Lo que vas a lograr con Barre Resuelto
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-48" data-mp="30vnce3rydidg">
            {[{
            title: 'Ahorrá Horas de Planificación',
            desc: 'Dejá de pasar horas buscando ideas en Instagram o Pinterest. Vas a tener clases organizadas y listas para aplicar.'
          }, {
            title: 'Clases Más Creativas y Variadas',
            desc: 'Aprendé a combinar ejercicios y crear secuencias diferentes para que ninguna clase vuelva a sentirse repetitiva.'
          }, {
            title: 'Más Seguridad al Enseñar',
            desc: 'Entrá a cada clase sabiendo exactamente qué hacer y cómo hacer que todo fluya de principio a fin.'
          }, {
            title: 'Alumnas Más Motivadas',
            desc: 'Diseñá clases dinámicas y entretenidas que hagan que tus alumnas disfruten cada sesión y quieran volver.'
          }, {
            title: 'Menos Estrés, Más Disfrute',
            desc: 'Dejá atrás la improvisación y volvé a disfrutar enseñar, enfocándote en lo que más te gusta.'
          }].map((benefit, idx) => <div key={idx} className={`bg-white p-6 rounded-2xl shadow-soft flex items-start gap-4 ${idx === 4 ? 'md:col-span-2 md:max-w-md md:mx-auto' : ''}`} data-id="element-49" data-mp="iqvwp85jtir2" data-mpi={idx}>
                <div className="bg-brand-babypink p-2 rounded-full flex-shrink-0 mt-1" data-id="element-50" data-mp="3m7vwzmt7kp49">
                  <CheckCircle2 size={24} className="text-brand-pink" data-id="element-51" data-mp="3r65sxzmrbtqx" />
                </div>
                <div data-id="element-52" data-mp="3w3p95530mgli">
                  <h3 className="font-bold text-lg mb-2" data-id="element-53" data-mp="3w362ikitfems">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm" data-id="element-54" data-mp="4ql4o1si5y4b">{benefit.desc}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-12 flex justify-center" data-id="element-55" data-mp="33dz19drz8axu">
            <PurchaseButton data-id="element-56" data-mp="38dlgttva239t" />
          </div>
        </div>
      </section>

      {/* CURRICULUM SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-57" data-mp="2f6lozbq8r2wf">
        <div className="max-w-3xl mx-auto" data-id="element-58" data-mp="2k5yant5dm3xf">
          <div className="text-center mb-10" data-id="element-59" data-mp="2k4x2pxjnpxjx">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-60" data-mp="2p5b795m1upd7">
              BARRE RESUELTO
            </h2>
            <p className="text-xl text-brand-pink font-medium" data-id="element-61" data-mp="2u4nuvskqw0gu">
              📚 Formación completa para profes de Barre
            </p>
          </div>

          <div className="space-y-2" data-id="element-62" data-mp="1wej5ztq4naj0">
            <AccordionItem title="🧱 TOMO 1 — Tu clase de barre de principio a fin: la estructura que necesitás" data-id="element-63" data-mp="21f77j9bzf9rh">
              <ul className="space-y-3" data-id="element-64" data-mp="26ds7d7nbnrfe">
                <li className="flex items-start gap-2" data-id="element-65" data-mp="26cqio0ddv6f7">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-66" data-mp="2b6lbpuevoxqy" />{' '}
                  Por qué la mayoría de las clases de barre se sienten
                  improvisadas
                </li>
                <li className="flex items-start gap-2" data-id="element-67" data-mp="3vz8qg7b1hgfj">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-68" data-mp="3vyz18lxtjdaw" />{' '}
                  La estructura base de una clase de 55 minutos
                </li>
                <li className="flex items-start gap-2" data-id="element-69" data-mp="3hzpv6f3btzkn">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-70" data-mp="3hyy0r7lgu7vy" />{' '}
                  El calentamiento
                </li>
                <li className="flex items-start gap-2" data-id="element-71" data-mp="388d3bnhj39em">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-72" data-mp="347t66jwyg5sh" />{' '}
                  El trabajo en barre
                </li>
                <li className="flex items-start gap-2" data-id="element-73" data-mp="2uaogia3whs6x">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-74" data-mp="2uc9anymaz949" />{' '}
                  Centro, piso y cierre
                </li>
                <li className="flex items-start gap-2" data-id="element-75" data-mp="2fpst43316rfx">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-76" data-mp="2knc5exg64u58" />{' '}
                  Cómo hacer que una clase fluya naturalmente
                </li>
                <li className="flex items-start gap-2" data-id="element-77" data-mp="2aqfu6jyvxtwz">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-78" data-mp="2ydo279np7h2p" />{' '}
                  Errores comunes de estructura
                </li>
                <li className="flex items-start gap-2" data-id="element-79" data-mp="2pkuin017xvj1">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-80" data-mp="2tmz7llgq23zc" />{' '}
                  Cómo adaptar la intensidad según el grupo
                </li>
                <li className="flex items-start gap-2" data-id="element-81" data-mp="3m4jz2l70h2gt">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-82" data-mp="3m5cu4owly3j4" />{' '}
                  Qué hacer cuando no sabés qué sigue
                </li>
                <li className="flex items-start gap-2" data-id="element-83" data-mp="1kevya7g189vz">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-84" data-mp="1kdky0hlspk9m" />{' '}
                  Clase modelo completa
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="💪 TOMO 2 — Biblioteca de ejercicios y variaciones" data-id="element-85" data-mp="mrdrv39ufjv5">
              <ul className="space-y-3" data-id="element-86" data-mp="11oe7w9io9qds">
                <li className="flex items-start gap-2" data-id="element-87" data-mp="11pfv1ccyq3bv">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-88" data-mp="wo8p7iudre7u" />{' '}
                  Ejercicios para piernas
                </li>
                <li className="flex items-start gap-2" data-id="element-89" data-mp="284qaqnwfvwfp">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-90" data-mp="2869r0ei3i7hh" />{' '}
                  Ejercicios para glúteos
                </li>
                <li className="flex items-start gap-2" data-id="element-91" data-mp="qnht69jch6p2">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-92" data-mp="qmq5rlzwif9v" />{' '}
                  Ejercicios para brazos
                </li>
                <li className="flex items-start gap-2" data-id="element-93" data-mp="10l5z7vmei0mj">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-94" data-mp="15k8tcl0uemfq" />{' '}
                  Ejercicios para abdomen
                </li>
                <li className="flex items-start gap-2" data-id="element-95" data-mp="1edc6stbhmzad">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-96" data-mp="1eex0yhtw4g7p" />{' '}
                  Ejercicios para espalda y postura
                </li>
                <li className="flex items-start gap-2" data-id="element-97" data-mp="1yex9loqn0fw1">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-98" data-mp="1ygi3rd91hwtd" />{' '}
                  Variaciones para principiantes
                </li>
                <li className="flex items-start gap-2" data-id="element-99" data-mp="1oj3q1lei3m4r">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-100" data-mp="1kgh23vkyalcu" />{' '}
                  Variaciones para alumnas avanzadas
                </li>
                <li className="flex items-start gap-2" data-id="element-101" data-mp="2c75234aw1kox">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-102" data-mp="27fd7miom2uti" />{' '}
                  Cómo aumentar o disminuir la intensidad
                </li>
                <li className="flex items-start gap-2" data-id="element-103" data-mp="2zwzejkrap78n">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-104" data-mp="2v5ha2mq2coix" />{' '}
                  Combinaciones de ejercicios
                </li>
                <li className="flex items-start gap-2" data-id="element-105" data-mp="y7jrf149jo02">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-106" data-mp="y937orpx5z1u" />{' '}
                  Progresiones simples
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="🎨 TOMO 3 — Cómo crear clases creativas y diferentes" data-id="element-107" data-mp="ah675wrx6tg1">
              <ul className="space-y-3" data-id="element-108" data-mp="fh2276in3ajg">
                <li className="flex items-start gap-2" data-id="element-109" data-mp="fi3m80hmv7db">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-110" data-mp="jrpxuy6a0mo" />{' '}
                  Cómo dejar de repetir siempre lo mismo
                </li>
                <li className="flex items-start gap-2" data-id="element-111" data-mp="1uykmbw6vqvfo">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-112" data-mp="1zwdu2tntzxi2" />{' '}
                  Métodos para combinar ejercicios
                </li>
                <li className="flex items-start gap-2" data-id="element-113" data-mp="3fftq584btctu">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-114" data-mp="3fd8q8onl96yq" />{' '}
                  Secuencias que funcionan
                </li>
                <li className="flex items-start gap-2" data-id="element-115" data-mp="3pbof0kz58452">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-116" data-mp="6y72lcvgzf49" />{' '}
                  Cómo crear bloques dinámicos
                </li>
                <li className="flex items-start gap-2" data-id="element-117" data-mp="gwwnleinxfoq">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-118" data-mp="by35f8sfl0i8" />{' '}
                  Clases temáticas
                </li>
                <li className="flex items-start gap-2" data-id="element-119" data-mp="2dqrevzvo4k6g">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-120" data-mp="2ds2fxrxqbdtv" />{' '}
                  Cómo mantener motivadas a tus alumnas
                </li>
                <li className="flex items-start gap-2" data-id="element-121" data-mp="21mvk8ykhpyry">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-122" data-mp="21ndooihb3jay" />{' '}
                  Ideas para variar las clases
                </li>
                <li className="flex items-start gap-2" data-id="element-123" data-mp="190142duphlka">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-124" data-mp="1dy2iglh6swpf" />{' '}
                  Cómo hacer transiciones fluidas
                </li>
                <li className="flex items-start gap-2" data-id="element-125" data-mp="1nws4mqg493bh">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-126" data-mp="2ubspo13ls13a" />{' '}
                  Ritmo y energía de la clase
                </li>
                <li className="flex items-start gap-2" data-id="element-127" data-mp="nna9ywhs243m">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-128" data-mp="notq8n3fof5e" />{' '}
                  Clase modelo creativa
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="🤸‍♀️ TOMO 4 — Adaptaciones y modificaciones" data-id="element-129" data-mp="8x321apec3h6">
              <ul className="space-y-3" data-id="element-130" data-mp="3zjcvfr5u6ag">
                <li className="flex items-start gap-2" data-id="element-131" data-mp="40ck0hu16xt4">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-132" data-mp="3w2kiong6a7kd" />{' '}
                  Cómo trabajar con alumnas principiantes
                </li>
                <li className="flex items-start gap-2" data-id="element-133" data-mp="3m5xwnvc41agr">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-134" data-mp="1pc4769iktle8" />{' '}
                  Adaptaciones para distintos niveles
                </li>
                <li className="flex items-start gap-2" data-id="element-135" data-mp="2c7lzxg037d64">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-136" data-mp="2c96sj02ucm1c" />{' '}
                  Cómo corregir sin interrumpir la clase
                </li>
                <li className="flex items-start gap-2" data-id="element-137" data-mp="1xipwjw8z6obu">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-138" data-mp="1xi6co9zrlpv6" />{' '}
                  Opciones de menor impacto
                </li>
                <li className="flex items-start gap-2" data-id="element-139" data-mp="1ojukw4n5wm15">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-140" data-mp="1jjq40dicdpae" />{' '}
                  Opciones de mayor intensidad
                </li>
                <li className="flex items-start gap-2" data-id="element-141" data-mp="3dw68p9vhovd5">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-142" data-mp="38x3dsi9845iw" />{' '}
                  Cómo dar alternativas durante una misma clase
                </li>
                <li className="flex items-start gap-2" data-id="element-143" data-mp="3lewaaf827m1w">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-144" data-mp="3q77alj6pp9uz" />{' '}
                  Errores frecuentes
                </li>
                <li className="flex items-start gap-2" data-id="element-145" data-mp="2yg16ysrs8u0y">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-146" data-mp="2yhkn8jdfv52q" />{' '}
                  Cómo manejar grupos con distintos niveles
                </li>
                <li className="flex items-start gap-2" data-id="element-147" data-mp="3cfiz9i4aepf2">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-148" data-mp="3cgc1c5sawqqv" />{' '}
                  Indicaciones claras y efectivas
                </li>
                <li className="flex items-start gap-2" data-id="element-149" data-mp="rngx0cf2411x">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-150" data-mp="20d1w9ztqjij" />{' '}
                  Clase modelo adaptable
                </li>
              </ul>
            </AccordionItem>

            <AccordionItem title="🗓️ TOMO 5 — Planificación y organización para profes" data-id="element-151" data-mp="bwzmqys5hakk">
              <ul className="space-y-3" data-id="element-152" data-mp="6y66wzpe57h8">
                <li className="flex items-start gap-2" data-id="element-153" data-mp="3ffj6te0oda4a">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-154" data-mp="3fdyb3l2mjl4u" />{' '}
                  Cómo planificar sin perder horas
                </li>
                <li className="flex items-start gap-2" data-id="element-155" data-mp="3kf5i3hwy0gag">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-156" data-mp="3kdknxtejizd4" />{' '}
                  Planificación semanal
                </li>
                <li className="flex items-start gap-2" data-id="element-157" data-mp="2rpyieflr5knm">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-158" data-mp="177xhfof28xh2" />{' '}
                  Planificación mensual
                </li>
                <li className="flex items-start gap-2" data-id="element-159" data-mp="xb18jhl929bz">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-160" data-mp="x8ezfdipnlrn" />{' '}
                  Cómo evitar la improvisación
                </li>
                <li className="flex items-start gap-2" data-id="element-161" data-mp="1uy04tr815f9c">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-162" data-mp="1uxi3ig6igauk" />{' '}
                  Cómo organizar tus ideas
                </li>
                <li className="flex items-start gap-2" data-id="element-163" data-mp="1ky10k6in8b2a">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-164" data-mp="5iu197rmw9a9" />{' '}
                  Qué hacer cuando te quedás sin inspiración
                </li>
                <li className="flex items-start gap-2" data-id="element-165" data-mp="pteg386l3nya">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-166" data-mp="l0lbckb48fm7" />{' '}
                  Cómo ahorrar tiempo preparando clases
                </li>
                <li className="flex items-start gap-2" data-id="element-167" data-mp="b3yq3uevnmjn">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-168" data-mp="18qnz4o7zyzf0" />{' '}
                  Organización para profes ocupadas
                </li>
                <li className="flex items-start gap-2" data-id="element-169" data-mp="yryrrpdq8xd0">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-170" data-mp="yukzbp0mbcv8" />{' '}
                  Calendario de clases
                </li>
                <li className="flex items-start gap-2" data-id="element-171" data-mp="3jgmlf53paga9">
                  <Heart size={16} className="text-brand-pink mt-1 flex-shrink-0" data-id="element-172" data-mp="3fg2jlo86mohs" />{' '}
                  Sistema de planificación editable
                </li>
              </ul>
            </AccordionItem>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-16 px-4 bg-brand-babypink" data-id="element-173" data-mp="23xyd5fak9ue7">
        <div className="max-w-6xl mx-auto" data-id="element-174" data-mp="28rjb57c5mode">
          <div className="text-center mb-12" data-id="element-175" data-mp="28scerzftgxrb">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-176" data-mp="2doj9sx6ocouh">
              Profes que ya llevaron sus clases al siguiente nivel
            </h2>
            <p className="text-xl text-gray-600" data-id="element-177" data-mp="2mq9a6sijay4e">
              Más de 500 profes de Argentina ya usan nuestro sistema
            </p>
          </div>

          <TestimonialsCarousel data-id="element-178" data-mp="2ro2ips7b847u" />
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-brand-lilac/20" data-id="element-179" data-mp="sdjhge3wjuyl">
        <div className="max-w-3xl mx-auto" data-id="element-180" data-mp="xbciksqclys0">
          <div className="text-center mb-10" data-id="element-181" data-mp="xc48bmx9l2ad">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-brand-pink" data-id="element-182" data-mp="20m8l18e9vkz2">
              Oferta por tiempo limitado
            </h2>
            <p className="text-xl font-medium" data-id="element-183" data-mp="1vogscc5szutt">
              Llevate estos BONOS GRATIS hoy
            </p>
          </div>

          <div className="space-y-6" data-id="element-184" data-mp="29ktzarcpodu1">
            {[{
            title: '🎁 Bono 1 — 100 ejercicios de barre',
            desc: 'Biblioteca completa de ejercicios para todo el cuerpo.',
            image: 'https://cdn.magicpatterns.com/uploads/bEBJUJG1PWSSetB3vLggx7/WhatsApp_Image_2026-06-19_at_7.48.49_PM.jpg'
          }, {
            title: '🎁 Bono 2 — 30 clases listas para usar',
            desc: 'Clases completas con estructura, duración y variantes.',
            image: 'https://cdn.magicpatterns.com/uploads/e2QFUqjhhnqJTF7jekEbUt/WhatsApp_Image_2026-06-19_at_7.48.52_PM.jpg'
          }, {
            title: '🎁 Bono 3 — Secuencias y combinaciones listas',
            desc: 'Bloques de ejercicios para crear clases más dinámicas.',
            image: 'https://cdn.magicpatterns.com/uploads/f242W92Cq6FJSxN1RgpkDT/WhatsApp_Image_2026-06-19_at_7.48.54_PM.jpg'
          }, {
            title: '🎁 Bono 4 — Guía para profes sin inspiración',
            desc: 'Qué hacer cuando sentís que repetís siempre lo mismo.',
            image: 'https://cdn.magicpatterns.com/uploads/69fKL2fqxWvRkuEmqbBeVo/WhatsApp_Image_2026-06-19_at_7.48.56_PM.jpg'
          }, {
            title: '🎁 Bono 5 — Atlas visual de ejercicios',
            desc: 'Fichas visuales con posiciones, variantes y objetivos.',
            image: 'https://cdn.magicpatterns.com/uploads/uQSJQGBr9aaFdKnhSyba7X/WhatsApp_Image_2026-06-19_at_7.48.59_PM.jpg'
          }, {
            title: '🎁 Bono 6 — Planillas de planificación para profes',
            desc: 'Plantillas editables para organizar semanas y meses.',
            image: 'https://cdn.magicpatterns.com/uploads/e2QFUqjhhnqJTF7jekEbUt/WhatsApp_Image_2026-06-19_at_7.48.52_PM.jpg'
          }].map((bono, idx) => <div key={idx} className="bg-white rounded-2xl shadow-soft border border-brand-pastelpink overflow-hidden" data-id="element-185" data-mp="2krzht8eyva80" data-mpi={idx}>
                {/* PREVIEW IMAGE WITH GRADIENT */}
                <a href="https://barreresuelto.impultienda.ar" className="block relative h-44 md:h-52 overflow-hidden group" data-id="element-186" data-mp="2bz61r8rp46t3">
                  <img src={bono.image} alt={bono.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" data-id="element-187" data-mp="223m4ahxhh028" />
                  {/* Degrade overlay fading into the card */}
                  <div className="absolute inset-0 bg-gradient-to-b from-brand-pink/20 via-transparent to-white pointer-events-none" data-id="element-188" data-mp="8j0zg9bcyova" />
                  <span className="absolute top-3 left-3 bg-brand-pink text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm" data-id="element-189" data-mp="hk8ywupm1vqy">
                    Vista previa
                  </span>
                </a>

                <AccordionItem title={bono.title} data-id="element-190" data-mp="3l20jho1f3df4">{bono.desc}</AccordionItem>
              </div>)}
          </div>

          <div className="mt-12 flex justify-center" data-id="element-191" data-mp="3q0bva4b9vxzz">
            <PurchaseButton data-id="element-192" data-mp="3uyn4cctqu4h5" />
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-193" data-mp="3kr3hwht7k8m">
        <div className="max-w-2xl mx-auto text-center" data-id="element-194" data-mp="3mkf7q7a4unh">
          <span className="inline-block bg-brand-pink text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4" data-id="element-195" data-mp="329g85khrhqz2">
            Solo por hoy
          </span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3" data-id="element-196" data-mp="2gz5ep61fc7sk">
            Esto es todo lo que te llevás
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-197" data-mp="2lxfbfl2srgga">
            Acceso inmediato y de por vida a Barre Resuelto más todos los bonos
            de regalo.
          </p>

          {/* COUNTDOWN UNDER TITLE/SUBTITLE */}
          <div className="flex flex-col items-center mb-8" data-id="element-198" data-mp="2byzjjfvy435q">
            <p className="text-brand-pink font-bold uppercase tracking-wide text-sm" data-id="element-199" data-mp="1y8w0exsd4pki">
              La oferta termina en
            </p>
            <CountdownTimer data-id="element-200" data-mp="231gum0f6l4ok" />
          </div>

          {/* VALUE STACK CARD */}
          <div className="bg-brand-babypink rounded-3xl shadow-soft border border-brand-pastelpink p-6 md:p-8 text-left" data-id="element-201" data-mp="1ocauma8irafm">
            <ul className="space-y-3" data-id="element-202" data-mp="1tacc4qqar1oz">
              {[{
              icon: <Heart size={18} className="text-brand-pink" data-id="element-203" data-mp="1fatif2y8fsw7" />,
              label: 'Formación completa (Tomos 1 al 5)',
              value: '$49.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-204" data-mp="10o4gyvkf75n6" />,
              label: 'Bono 1 — 100 ejercicios de barre',
              value: '$9.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-205" data-mp="19plz0jhe1hei" />,
              label: 'Bono 2 — 30 clases listas para usar',
              value: '$9.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-206" data-mp="1jkdvfjz8hy7p" />,
              label: 'Bono 3 — Secuencias y combinaciones',
              value: '$7.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-207" data-mp="1te324fvhuimk" />,
              label: 'Bono 4 — Guía para profes sin inspiración',
              value: '$5.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-208" data-mp="1xdvfr6zqveys" />,
              label: 'Bono 5 — Atlas visual de ejercicios',
              value: '$7.990'
            }, {
              icon: <Gift size={18} className="text-brand-pink" data-id="element-209" data-mp="zzafu9z9avlg" />,
              label: 'Bono 6 — Planillas de planificación',
              value: '$5.990'
            }, {
              icon: <InfinityIcon size={18} className="text-brand-pink" data-id="element-210" data-mp="18sdq69elusbv" />,
              label: 'Acceso de por vida + futuras mejoras',
              value: 'Incluido'
            }].map((item, idx) => <li key={idx} className="flex items-center justify-between gap-3 border-b border-brand-pastelpink/60 pb-3 last:border-0 last:pb-0" data-id="element-211" data-mp="1ioqib2ui4y4i" data-mpi={idx}>
                  <span className="flex items-center gap-2 text-sm text-brand-black" data-id="element-212" data-mp="1rpeya33gh8k1">
                    <span className="bg-white p-1.5 rounded-full flex-shrink-0" data-id="element-213" data-mp="3phj7viuqjaif">
                      {item.icon}
                    </span>
                    {item.label}
                  </span>
                  <span className="text-sm text-gray-400 line-through whitespace-nowrap" data-id="element-214" data-mp="3lzlqg1sks94q">
                    {item.value}
                  </span>
                </li>)}
            </ul>

            <div className="mt-6 pt-5 border-t-2 border-dashed border-brand-pink/40 text-center" data-id="element-215" data-mp="3c9sg14466022">
              <p className="text-sm text-gray-500" data-id="element-216" data-mp="3886w4yt7wlid">
                Valor real total:{' '}
                <span className="line-through font-semibold" data-id="element-217" data-mp="8sa9d004uz38">$97.920</span>
              </p>
              <div className="flex items-center justify-center gap-3 mt-1" data-id="element-218" data-mp="3sfeymh5frdf">
                <span className="text-gray-400 line-through text-lg" data-id="element-219" data-mp="3vwq237osom7a">
                  $63.950
                </span>
                <span className="text-4xl md:text-5xl font-serif font-bold text-brand-pink" data-id="element-220" data-mp="3qy6gx9i0zefz">
                  $21.990
                </span>
              </div>
              <p className="inline-flex items-center gap-1 text-xs font-bold text-white bg-brand-pink rounded-full px-3 py-1 mt-3" data-id="element-221" data-mp="2kfi1kxh9rq8e">
                <Sparkles size={14} data-id="element-222" data-mp="79a0486i6hxh" /> Ahorrás más de $75.000 hoy
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center mt-8" data-id="element-223" data-mp="c233armbpi7g">
            <PurchaseButton className="scale-105 hover:scale-110 mt-2" data-id="element-224" data-mp="3uf193f5sr59j" />
            <p className="text-sm text-gray-500 mt-4 flex items-center justify-center gap-2" data-id="element-225" data-mp="3ucoi6ci29kkc">
              <Lock size={16} className="text-green-500" data-id="element-226" data-mp="28chxvlhtnvz" />
              Pago 100% seguro · Acceso inmediato
            </p>
          </div>
        </div>
      </section>
    </div>;
}